// A bottom sheet for a phone: the Breeze chat on the prep page, the whole prep
// workspace in a call. The page behind stops scrolling while it is open, the
// scrim closes it, and the sheet rides above the keyboard.

import { useEffect, useRef, type JSX, type ReactNode } from "react";
import { CloseIcon } from "./icons";

interface WorkspaceSheetProps {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** Fill the screen instead of 78% of it. */
  readonly tall?: boolean;
  /** A panel down the right edge, with the call still usable beside it
   * (the chat on a desktop). */
  readonly side?: boolean;
}

export function WorkspaceSheet({ title, onClose, children, tall = false, side = false }: WorkspaceSheetProps): JSX.Element {
  const ref = useRef<HTMLElement>(null);

  // Scroll lock: the page behind keeps its place and does not move.
  useEffect(() => {
    if (side) return;
    const html = document.documentElement;
    const body = document.body;
    const before = { html: html.style.overflow, body: body.style.overflow, touch: body.style.touchAction };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.touchAction = "none";
    return () => {
      html.style.overflow = before.html;
      body.style.overflow = before.body;
      body.style.touchAction = before.touch;
    };
  }, [side]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Above the keyboard: the visual viewport shrinks when it opens.
  useEffect(() => {
    const vv = window.visualViewport;
    const el = ref.current;
    if (!vv || !el) return;
    const fit = (): void => {
      const lift = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      el.style.bottom = `${lift}px`;
      el.style.maxHeight = `${vv.height}px`;
    };
    fit();
    vv.addEventListener("resize", fit);
    vv.addEventListener("scroll", fit);
    return () => {
      vv.removeEventListener("resize", fit);
      vv.removeEventListener("scroll", fit);
    };
  }, []);

  return (
    <>
      <div className={`ws-scrim${side ? " ws-scrim--side" : ""}`} onClick={onClose} />
      <section ref={ref} className={`ws${tall ? " ws--tall" : ""}${side ? " ws--side" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="ws__head">
          <h2 className="ws__title">{title}</h2>
          <button type="button" className="ws__close" aria-label={`Close ${title}`} onClick={onClose}>
            <CloseIcon size={20} />
          </button>
        </header>
        <div className="ws__body">{children}</div>
      </section>
    </>
  );
}
